import Box from '@mui/material/Box'
import { observer } from 'mobx-react-lite'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import GapLink from 'reusable/mui/links/GapLink'
import Typography from '@mui/material/Typography'
import { PlanSelection } from 'features/org/edit/billing/PlanSelection'
import { themeConfig }from '@gap/shared/theme'
import Button from '@mui/material/Button'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import CheckCircle from '@mui/icons-material/CheckCircle'
import useWindowSize from 'reusable/hooks/useWindowSize'
import { PlatformContainerDialog } from 'reusable/containers/PlatformContainerDialog'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { useTheme } from 'theme/useTheme'
import variables from 'variables/variables'
import { useOrgSubscriptionTierStore } from 'features/main/main-router/org-guard/main-container/org-subscription-tiers/OrgSubscriptionTierStore'

export default observer(function OrgUpgrade({ state }: { state: ModalState }) {
   const { screenLayout } = useWindowSize()
   const theme = useTheme()
   const orgSubscriptionTierStore = useOrgSubscriptionTierStore()

   return <PlatformContainerDialog variant={screenLayout === 'mobile' ? 'plain' : 'tinted'} {...{ state }}>
      {screenLayout === 'mobile'
         ? <Stack sx={{ paddingY: 3 }}>{orgSubscriptionTierStore.ViewComponent}</Stack>
         : <Paper sx={{
            width: '100%',
            maxWidth: theme.breakpoints.values.lg,
            borderRadius: '16px',
            padding: 6,
         }}>
            {orgSubscriptionTierStore.ViewComponent}
         </Paper>
      }
   </PlatformContainerDialog>
})

// The plan picker. NO-455 interim: shown to trial orgs (who convert by picking a plan) and
// orgs already on a new plan — choosing a plan upgrades the account immediately and sales
// arranges billing. Legacy paid orgs are routed to <RequestMoreSeats /> instead.
export const UpgradeTierInfo = observer(function UpgradeTierInfo() {
   const orgSubscriptionTierStore = useOrgSubscriptionTierStore()

   return <>
      {orgSubscriptionTierStore.orgIsOnTrial
         ? <>
            <Typography variant="h4" sx={{ color: themeConfig.primary[900] }}>
               {orgSubscriptionTierStore.orgIsOnExpiredTrial
                  ? <>Your trial has ended, please choose a plan to continue</>
                  : <>Choose a plan</>}
            </Typography>
            <Typography variant="body1" sx={{ color: themeConfig.primary[900], mt: 1 }}>
               One subscription, a bunch of great benefits to make advisory easy.
               With plans designed to scale with you and your team.
            </Typography>
         </>
         : <>
            <Typography variant="h4" sx={{ color: themeConfig.primary[900], textAlign: 'center' }}>Your team must be growing!</Typography>
            <Typography variant="body1" sx={{ color: themeConfig.primary[900], marginTop: 1, textAlign: 'center' }}>Upgrade your plan to invite more team members</Typography>
         </>}
      <Box sx={{ mt: 6 }}>
         <PlanSelection />
      </Box>
      <Stack sx={{ gap: 2, mt: 6 }}>
         <Typography variant="body2" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            When you choose a plan, your account will immediately upgrade and a member of our sales team will be in touch to arrange billing.
         </Typography>
         <PricingPageLink />
      </Stack>
   </>
})

// Orgs on a legacy paid tier can't self-serve a plan change, so they ask our team to add seats.
// The button emails sales (and the requester) via notifySalesOfUpgrade.
// PR #471 (Chargebee) keeps this screen for paid orgs not yet migrated to Chargebee.
export const RequestMoreSeats = observer(function RequestMoreSeats() {
   const orgSubscriptionTierStore = useOrgSubscriptionTierStore()

   return <>
      <Typography variant="h4" sx={{ color: themeConfig.primary[900] }}>Need more seats?</Typography>
      <Typography variant="body1" sx={{ color: themeConfig.primary[900], mt: 1 }}>
         {orgSubscriptionTierStore.seatsInUseText} To add more team member seats, request more below and our team
         will be in touch to update your plan.
      </Typography>
      <Box textAlign="center">
         <ButtonWithProgress
            sx={{ mt: 7 }}
            variant="contained"
            loading={orgSubscriptionTierStore.requestLoader.isLoading}
            onClick={orgSubscriptionTierStore.requestMoreSeats}
         >
            Request more seats
         </ButtonWithProgress>
      </Box>
   </>
})

// Confirmation shown after the seat request email has been sent.
export const RequestSent = observer(function RequestSent() {
   const orgSubscriptionTierStore = useOrgSubscriptionTierStore()

   return <>
      <Box textAlign="center">
         <CheckCircle sx={{ color: themeConfig.system.success[600] }} width="30px" height="30px" />
      </Box>
      <Typography variant="h4" textAlign="center" sx={{ color: themeConfig.primary[900] }}>Request sent</Typography>
      <Typography variant="body1" textAlign="center" sx={{ color: themeConfig.primary[900], marginTop: 1 }}>
         Our team has been notified and will be in touch shortly to update your plan.
      </Typography>
      <Box textAlign="center">
         <Button sx={{ mt: 7 }} variant="contained" onClick={orgSubscriptionTierStore.finishRequest}>
            Continue
         </Button>
      </Box>
   </>
})

const PricingPageLink = observer(function PricingPageLink() {
   return (
      <GapLink variant="caption" href={variables.pricingUrl} target="_blank" underlineStyle="dashed" sx={{
         textAlign: { xs: 'center', sm: 'left' },
      }}>See pricing page for more info</GapLink>
   )
})
